import { format as formatDateFns, isValid } from 'date-fns';

/**
 * Coerce a possibly-string numeric value to a finite number.
 *
 * Postgres `decimal` / `numeric` columns are serialised as strings by the
 * driver (e.g. `"12.50"`), so a value that TypeScript types as `number` can
 * actually arrive as a string at runtime. Calling `.toFixed()` on it then
 * throws `TypeError: x.toFixed is not a function` and crashes the whole page
 * (the error boundary shows a generic "500"). Wrap such values in `num()`
 * before formatting. null / undefined / non-numeric → 0.
 */
export function num(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Safely format a date with date-fns. Returns `fallback` for null/empty/invalid
 * values instead of throwing `RangeError: Invalid time value` — which crashes the
 * whole page when it happens inside a table cell renderer. Date-only strings
 * (YYYY-MM-DD) are pinned to local midnight to avoid timezone drift.
 */
export function safeFormatDate(
  value: string | number | Date | null | undefined,
  pattern: string,
  fallback = '—',
): string {
  if (value === null || value === undefined || value === '') return fallback;
  const date =
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T00:00:00`)
      : new Date(value);
  return isValid(date) ? formatDateFns(date, pattern) : fallback;
}

/**
 * Format a number as currency.
 */
export function formatCurrency(
  amount: number,
  currency = 'USD',
  locale = 'en-US',
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Format a date string to a human-readable format.
 */
export function formatDate(
  date: string | Date,
  options?: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  }).format(new Date(date));
}

/**
 * Format a relative time (e.g., "2 hours ago").
 */
export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const diff = now.getTime() - new Date(date).getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return formatDate(date);
}

/**
 * Truncate text to a maximum length.
 */
export function truncate(text: string, maxLength = 50): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/**
 * Raw UUIDs should never render as-is in the UI — shorten to the
 * `slice(0, 8) + '…'` form already used across the app for record ids.
 */
export function formatShortId(id: string): string {
  return isUuid(id) ? `${id.slice(0, 8)}…` : id;
}

/**
 * `UPPER_SNAKE_CASE` (or `lower_snake_case`) → "Title Case" for user-facing
 * labels. The backend returns enum values as stored; this is the one place
 * that turns e.g. `UNDER_INVESTIGATION` into "Under Investigation" for
 * display and export — use it instead of rendering an enum value verbatim.
 */
export function humanizeEnum(value: string | null | undefined): string {
  if (!value) return '—';
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Like `humanizeEnum`, but for callers rendering an arbitrary/dynamic column
 * that mixes enum-shaped values with genuine free text (e.g. a generic
 * report table built from a column-config array, where the code doesn't know
 * which columns are enums). Only reformats values that actually look like a
 * backend enum (`ALL_CAPS_WITH_UNDERSCORES`) — anything else passes through
 * unchanged so real free text isn't mangled.
 */
export function humanizeIfEnumLike(value: string): string {
  return /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/.test(value) ? humanizeEnum(value) : value;
}

/**
 * Get user initials from first and last name.
 */
export function getInitials(firstName?: string, lastName?: string): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';
}

const STORAGE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;
export type StorageUnit = (typeof STORAGE_UNITS)[number];

/**
 * Pick the most readable unit index for a byte count.
 * 0=B, 1=KB, 2=MB, 3=GB, 4=TB, 5=PB.
 */
export function pickStorageUnit(bytes: number): number {
  if (bytes <= 0) return 0;
  const idx = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.min(idx, STORAGE_UNITS.length - 1);
}

/**
 * Convert a byte count into a specific unit (returns the numeric value only).
 */
export function bytesToUnit(bytes: number, unitIndex: number): number {
  return bytes / Math.pow(1024, unitIndex);
}

/**
 * Format a byte count using the most readable unit.
 * 0 → "0 B", 1023 → "1023 B", 1024 → "1.00 KB", 1536000 → "1.46 MB", 5e9 → "4.66 GB".
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const idx = pickStorageUnit(bytes);
  const value = bytesToUnit(bytes, idx);
  const precision = value >= 100 ? 0 : value >= 10 ? 1 : decimals;
  return `${value.toFixed(precision)} ${STORAGE_UNITS[idx]}`;
}

/**
 * For a series of byte values (e.g. one per tenant), pick ONE shared unit
 * based on the maximum so all values are comparable in a chart.
 * Returns the chosen unit label, the converted values, and a sensible decimals count.
 */
export function formatBytesSeries(values: number[]): {
  unit: StorageUnit;
  converted: number[];
  decimals: number;
} {
  const max = values.length ? Math.max(...values) : 0;
  const idx = pickStorageUnit(max);
  const unit = STORAGE_UNITS[idx] ?? 'B';
  const converted = values.map((v) => bytesToUnit(v, idx));
  const decimals = max < 10 * Math.pow(1024, idx) ? 2 : max < 100 * Math.pow(1024, idx) ? 1 : 0;
  return { unit, converted, decimals };
}
